import React, { useState, useRef } from "react";
import {
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormHelperText,
} from "@mui/material";
import SectionCard from "./SectionCard";

const itrRows = [
  { assessmentYear: "2024-2025", financialYear: "2023-2024" },
  { assessmentYear: "2025-2026", financialYear: "2024-2025" },
];

const Step4Statutory = ({
  formData,
  setFieldValue,
  onStatutoryDuplicateBlur,
  errors,
  touched,
  paymentTermsOptions = [],
  isVendorUser = false,
}) => {
  const fieldError = (name) => Boolean(touched?.[name] && errors?.[name]);
  const fieldHelper = (name) => (touched?.[name] && errors?.[name]) || "";
  const STATUTORY_DUP_FLAG_BY_FIELD = {
    statutoryPanNo: "isPanNoAlreadyExists",
    statutoryGstNo: "isGstNoAlreadyExists",
    statutoryAccountNumber: "isAccNoAlreadyExists",
  };

  const handleChange = (e) => {
    if (!setFieldValue) return;
    const { name, value } = e.target;
    setFieldValue(name, value);
    const flag = STATUTORY_DUP_FLAG_BY_FIELD[name];
    if (flag) setFieldValue(flag, "N");
  };

  const handleFileUpload = (fieldName, file) => {
    if (setFieldValue) setFieldValue(fieldName, file || null);
  };

  const handleGstApplicableChange = (e) => {
    const value = e.target.value;
    setFieldValue("gstApplicable", value);
    if (value === "Y") {
      setFieldValue("gstSelfDeclarationAttachment", null);
    } else if (value === "N") {
      setFieldValue("statutoryGstNo", "");
      setFieldValue("gstCertificateAttachment", null);
      setFieldValue("isGstNoAlreadyExists", "N");
    }
  };

  const showGstDetails = formData.gstApplicable === "Y";
  const showGstSelfDeclaration = formData.gstApplicable === "N";

  const handleMsmeDeclarationChange = (e) => {
    const value = e.target.value;
    setFieldValue("msmeDeclaration", value);
    if (value === "N") {
      setFieldValue("msmeType", "");
      setFieldValue("msmeNo", "");
      setFieldValue("msmeValidityFromDate", "");
      setFieldValue("msmeValidityToDate", "");
      setFieldValue("msmeCertificateAttachment", null);
    } else if (value === "Y") {
      setFieldValue("msmeNoSignatureAttachment", null);
    }
  };

  const isProprietorship = Array.isArray(formData?.shareholdingStatus)
    ? formData.shareholdingStatus.some(
        (item) => String(item).toLowerCase() === "proprietorship",
      )
    : false;

  return (
    <Grid container spacing={2}>
      <SectionCard title="STATUTORY*">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              * MANDATORY FIELDS REQUIRED TO CREATE VENDOR CODE (ENSURE ALL
              DOCUMENTS ATTACHED BEFORE SUBMITTING FOR APPROVAL)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              ITR RETURN DETAILS* (Last two year ITR NO – AS 24-25 & AS 25-26)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                  
                    <TableCell>Tax Year</TableCell>
                    <TableCell>Acknowledgement Number for ITR field</TableCell>
                    <TableCell>Date of ITR filing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itrRows.map((row, i) => (
                    <TableRow key={i}>
                    
                      <TableCell>{row.financialYear}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name={`itrAcknowledgementNo${i + 1}`}
                          value={formData[`itrAcknowledgementNo${i + 1}`] || ""}
                          onChange={(e) => {
                            const fieldName = `itrAcknowledgementNo${i + 1}`;
                            const value = e.target.value
                              .replace(/[^a-zA-Z0-9]/g, "")
                              .slice(0, 15);
                            handleChange({
                              target: { name: fieldName, value },
                            });
                          }}
                          error={fieldError(`itrAcknowledgementNo${i + 1}`)}
                          helperText={fieldHelper(`itrAcknowledgementNo${i + 1}`)}
                          inputProps={{ maxLength: 15 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          name={`itrDateOfFiling${i + 1}`}
                          value={formData[`itrDateOfFiling${i + 1}`] || ""}
                          onChange={handleChange}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
             <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="PAN NO *"
              name="statutoryPanNo"
              value={formData.statutoryPanNo || ""}
              onChange={(e) => {
                const upperValue = e.target.value.toUpperCase();
                setFieldValue("statutoryPanNo", upperValue);
                const flag = STATUTORY_DUP_FLAG_BY_FIELD["statutoryPanNo"];
                if (flag) setFieldValue(flag, "N");
              }}
              onBlur={onStatutoryDuplicateBlur}
              error={fieldError("statutoryPanNo")}
              helperText={fieldHelper("statutoryPanNo")}
              inputProps={{ maxLength: 10 }}
            />
            <Box sx={{ mt: 1 }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileUpload("panScanAttachment", f);
                  e.target.value = "";
                }}
                style={{ fontSize: "12px" }}
              />
              {formData.panScanAttachment && (
                <Chip
                  size="small"
                  label={formData.panScanAttachment.name || "PAN scan"}
                  onDelete={() => handleFileUpload("panScanAttachment", null)}
                  sx={{ mt: 0.5 }}
                  variant="outlined"
                />
              )}
              {fieldError("panScanAttachment") && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {fieldHelper("panScanAttachment")}
                </FormHelperText>
              )}
            </Box>
          </Grid>
        <Grid item xs={12} sm={6}/>
          <Grid item xs={12} sm={6}>
            <FormControl
              component="fieldset"
              error={fieldError("gstApplicable")}
              fullWidth
            >
              <FormLabel component="legend">GST *</FormLabel>
              <RadioGroup
                row
                name="gstApplicable"
                value={formData.gstApplicable || ""}
                onChange={handleGstApplicableChange}
              >
                <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                <FormControlLabel value="N" control={<Radio />} label="No" />
              </RadioGroup>
              {fieldError("gstApplicable") && (
                <FormHelperText>{fieldHelper("gstApplicable")}</FormHelperText>
              )}
            </FormControl>

            {showGstDetails && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="GST NO (with Annexures) *"
                  name="statutoryGstNo"
                  value={formData.statutoryGstNo || ""}
                  onChange={handleChange}
                  onBlur={onStatutoryDuplicateBlur}
                  error={fieldError("statutoryGstNo")}
                  helperText={
                    fieldHelper("statutoryGstNo") ||
                    "A scanned copy of the required certificate must be provided. Any address change after vendor creation must be informed to the company within 7 days"
                  }
                  inputProps={{
                    maxLength: 15,
                  }}
                  sx={{ mt: 1 }}
                />
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload("gstCertificateAttachment", f);
                      e.target.value = "";
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  {formData.gstCertificateAttachment && (
                    <Chip
                      size="small"
                      label={
                        formData.gstCertificateAttachment.name ||
                        "GST certificate"
                      }
                      onDelete={() =>
                        handleFileUpload("gstCertificateAttachment", null)
                      }
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                    />
                  )}
                  {fieldError("gstCertificateAttachment") && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      {fieldHelper("gstCertificateAttachment")}
                    </FormHelperText>
                  )}
                </Box>
              </>
            )}

            {showGstSelfDeclaration && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Attach self declaration form
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileUpload("gstSelfDeclarationAttachment", f);
                    e.target.value = "";
                  }}
                  style={{ fontSize: "12px" }}
                />
                {formData.gstSelfDeclarationAttachment && (
                  <Chip
                    size="small"
                    label={
                      formData.gstSelfDeclarationAttachment.name ||
                      "GST self declaration"
                    }
                    onDelete={() =>
                      handleFileUpload("gstSelfDeclarationAttachment", null)
                    }
                    sx={{ mt: 0.5 }}
                    variant="outlined"
                  />
                )}
                {fieldError("gstSelfDeclarationAttachment") && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {fieldHelper("gstSelfDeclarationAttachment")}
                  </FormHelperText>
                )}
              </Box>
            )}
          </Grid>
       
          {isProprietorship && (
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  AADHAR & PAN LINK * (Need Vendor signature)
                </FormLabel>
                <RadioGroup
                  row
                  name="aadharLinkWithPan"
                  value={formData.aadharLinkWithPan || ""}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Y" control={<Radio />} label="YES" />
                  <FormControlLabel value="N" control={<Radio />} label="NO" />
                </RadioGroup>
              {/* <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                If Yes: required scan copy. If no: attach Vendor Signature
              </Typography>
              {formData.aadharLinkWithPan === "Y" && (
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload("aadharPanLinkAttachment", f);
                      e.target.value = "";
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  {formData.aadharPanLinkAttachment && (
                    <Chip
                      size="small"
                      label={
                        formData.aadharPanLinkAttachment.name || "Document"
                      }
                      onDelete={() =>
                        handleFileUpload("aadharPanLinkAttachment", null)
                      }
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                    />
                  )}
                </Box>
              )} */}
              {/* {formData.aadharLinkWithPan === "N" && (
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f)
                        handleFileUpload("aadharPanNoSignatureAttachment", f);
                      e.target.value = "";
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  {formData.aadharPanNoSignatureAttachment && (
                    <Chip
                      size="small"
                      label={
                        formData.aadharPanNoSignatureAttachment.name ||
                        "Vendor signature"
                      }
                      onDelete={() =>
                        handleFileUpload("aadharPanNoSignatureAttachment", null)
                      }
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                    />
                  )}
                </Box>
              )} */}
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                MSME declaration * (Need Vendor signature)
              </FormLabel>
              <RadioGroup
                row
                name="msmeDeclaration"
                value={formData.msmeDeclaration || ""}
                onChange={handleMsmeDeclarationChange}
              >
                <FormControlLabel value="Y" control={<Radio />} label="YES" />
                <FormControlLabel value="N" control={<Radio />} label="NO" />
              </RadioGroup>
              {fieldError("msmeDeclaration") && (
                <FormHelperText error>{fieldHelper("msmeDeclaration")}</FormHelperText>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
            If yes, a scanned copy of the required certificate is needed. If no, a signed declaration is required. The address must match the GST address, and it should be renewed once a year
              </Typography>
              {formData.msmeDeclaration === "Y" && (
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload("msmeCertificateAttachment", f);
                      e.target.value = "";
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  {formData.msmeCertificateAttachment && (
                    <Chip
                      size="small"
                      label={
                        formData.msmeCertificateAttachment.name ||
                        "MSME certificate"
                      }
                      onDelete={() =>
                        handleFileUpload("msmeCertificateAttachment", null)
                      }
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                    />
                  )}
                  {fieldError("msmeCertificateAttachment") && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      {fieldHelper("msmeCertificateAttachment")}
                    </FormHelperText>
                  )}
                </Box>
              )}
              {formData.msmeDeclaration === "N" && (
                <Box sx={{ mt: 1 }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload("msmeNoSignatureAttachment", f);
                      e.target.value = "";
                    }}
                    style={{ fontSize: "12px" }}
                  />
                  {formData.msmeNoSignatureAttachment && (
                    <Chip
                      size="small"
                      label={
                        formData.msmeNoSignatureAttachment.name ||
                        "Vendor signature"
                      }
                      onDelete={() =>
                        handleFileUpload("msmeNoSignatureAttachment", null)
                      }
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                    />
                  )}
                  {fieldError("msmeNoSignatureAttachment") && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      {fieldHelper("msmeNoSignatureAttachment")}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} sm={6} /> */}
          {/* If MSME yes then only show MSME Type and MSME No field */}
          {formData.msmeDeclaration === "Y" && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl error={!formData.msmeType?true:false} fullWidth size="small">
                  <InputLabel>MSME Type</InputLabel>
                  <Select
                    name="msmeType"
                    value={formData.msmeType || ""}
                    onChange={handleChange}
                    label="MSME Type"
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="Micro">Micro</MenuItem>
                    <MenuItem value="Small">Small</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                  </Select>
                   <FormHelperText>
                    {fieldHelper("msmeType")}
                    {fieldError("msmeType")}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="MSME No."
                  name="msmeNo"
                  value={formData.msmeNo || ""}
                  onChange={handleChange}
                  required
                    error={fieldError("msmeNo")}
                    helperText={fieldHelper("msmeNo")}
                    inputProps={{
                    maxLength: 16,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Registration Date"
                    name="msmeValidityFromDate"
                    value={formData.msmeValidityFromDate || ""}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={fieldError("msmeValidityFromDate")}
                    helperText={fieldHelper("msmeValidityFromDate")}

                  />
              </Grid>

                <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Lastest Classification date"
                    name="msmeValidityToDate"
                    value={formData.msmeValidityToDate || ""}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={fieldError("msmeValidityToDate")}
                    helperText={fieldHelper("msmeValidityToDate")}

                  />
              </Grid>

            </>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="ESI NO (If applicable)"
              name="esiNo"
              value={formData.esiNo || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="PF NO (If applicable)"
              name="pfNo"
              value={formData.pfNo || ""}
              onChange={handleChange}
            />
          </Grid>
          {!isVendorUser && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={fieldError("paymentTerms")}>
                <InputLabel id="payment-terms-label">PAYMENT TERMS</InputLabel>
                <Select
                  labelId="payment-terms-label"
                  label="PAYMENT TERMS"
                  name="paymentTerms"
                  value={formData.paymentTerms || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>Select Payment Terms</em>
                  </MenuItem>
                  {paymentTermsOptions.map((option) => {
                    const optionValue = option?.code ?? option?.name ?? "";
                    const optionKey =
                      option?.paymentTermsId ?? option?.id ?? optionValue;
                    const optionLabel = [option?.code, option?.name]
                      .filter(Boolean)
                      .join(" - ");
                    return (
                      <MenuItem key={optionKey} value={optionValue}>
                        {optionLabel || optionValue}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  Apart from normal terms, need HOD approval (based on cost sheet)
                </FormHelperText>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="BANK DETAILS*">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="BANK NAME"
              name="statutoryBankName"
              value={formData.statutoryBankName || ""}
              onChange={handleChange}
              required
               error={fieldError("statutoryBankName")}
              helperText={fieldHelper("statutoryBankName")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="BRANCH"
              name="branch"
              value={formData.branch || ""}
              onChange={handleChange}
              required
               error={fieldError("branch")}
            helperText={fieldHelper("branch")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="IFSC CODE"
              name="statutoryIfscCode"
              value={formData.statutoryIfscCode || ""}
              // onChange={handleChange}
               onChange={(e) => {
              const value = e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "");

              handleChange({
                target: {
                  name: "statutoryIfscCode",
                  value: value.slice(0, 11),
                },
              });
            }}
                      inputProps={{
                      maxLength: 11,
                    }}
                    error={fieldError("statutoryIfscCode")}
                  helperText={fieldHelper("statutoryIfscCode")}
                  required
                  />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="ACCOUNT NUMBER"
              name="statutoryAccountNumber"
              value={formData.statutoryAccountNumber || ""}
              // onChange={handleChange}
               onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");

                handleChange({
                  target: {
                    name: "statutoryAccountNumber",
                    value,
                  },
                });
              }}
              onBlur={onStatutoryDuplicateBlur}
              required
               error={fieldError("statutoryAccountNumber")}
              helperText={fieldHelper("statutoryAccountNumber")}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 18,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1, whiteSpace: "pre-line" }}
            >
              {`Attach a cancelled cheque leaf, copy of the passbook, or bank statement.
              The name and account number must be in printed format.
              Any change after vendor creation must be informed to the company immediately.`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileUpload("bankDocumentAttachment", f);
                e.target.value = "";
              }}
              style={{ fontSize: "12px" }}
            />
            {formData.bankDocumentAttachment && (
              <Chip
                size="small"
                label={formData.bankDocumentAttachment.name || "Bank document"}
                onDelete={() =>
                  handleFileUpload("bankDocumentAttachment", null)
                }
                sx={{ mt: 0.5 }}
                variant="outlined"
              />
            )}
            {fieldError("bankDocumentAttachment") && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {fieldHelper("bankDocumentAttachment")}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </SectionCard>
      <Box sx={{ width: "100%" }}>
      {/* <SectionCard title="Job Worker">
        <Grid container spacing={2} >
                <Grid item xs={12} sm={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Vendor Agreement Status
              </FormLabel>
              <RadioGroup
                row
                name="vendorAgreementStatus"
                value={formData.vendorAgreementStatus || ""}
                onChange={handleChange}
              >
                <FormControlLabel value="Y" control={<Radio />} label="YES" />
                <FormControlLabel value="N" control={<Radio />} label="NO" />
              </RadioGroup>
  
            </FormControl>
          </Grid>

          {/* If MSME yes then only show MSME Type and MSME No field */}
          {/* {formData.vendorAgreementStatus === "Y" && (
            <>

             <Grid item xs={12}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileUpload("vendorAgreementAttachment", f);
                e.target.value = "";
              }}
              style={{ fontSize: "12px" }}
            />
            {formData.vendorAgreementAttachment && (
              <Chip
                size="small"
                label={formData.vendorAgreementAttachment.name || "Vendor Agreement"}
                onDelete={() =>
                  handleFileUpload("vendorAgreementAttachment", null)
                }
                sx={{ mt: 0.5 }}
                variant="outlined"
              />
            )}
          </Grid>
   
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    name="vendorAgreementStatusFromDate"
                    value={formData.vendorAgreementStatusFromDate || ""}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={fieldError("vendorAgreementStatusFromDate")}
                    helperText={fieldHelper("vendorAgreementStatusFromDate")}

                  />
              </Grid>

                <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    name="vendorAgreementStatusToDate"
                    value={formData.vendorAgreementStatusToDate || ""}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={fieldError("vendorAgreementStatusToDate")}
                    helperText={fieldHelper("vendorAgreementStatusToDate")}

                  />
              </Grid>

            </>
          )}

           <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Security Cheque Details"
              name="securityChequeDetails"
              value={formData.securityChequeDetails || ""}
              onChange={handleChange}
              multiline
              rows={2}
              error={fieldError("securityChequeDetails")}
              helperText={fieldHelper("securityChequeDetails")}
            />
          </Grid>
         </Grid>
        </SectionCard> */}
        </Box>
    </Grid>
  );
};

export default Step4Statutory;
